import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Search } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { SoftIdentityGate } from '@/components/auth/SoftIdentityGate'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { formatAddressDisplay, splitAddressDisplay } from '@/lib/address'
import { callableErrorMessage } from '@/lib/callable-errors'
import {
  finalizeOwnershipVerification,
  initializeOwnershipVerification,
  searchPropertiesByPrefix,
  type PropertySuggestion,
} from '@/lib/callables'
import { db } from '@/lib/firebase'
import { parseProperty, type Property } from '@/types/models'

type Step = 'property' | 'selected' | 'confirm' | 'done'

const STEPS: Step[] = ['property', 'selected', 'confirm', 'done']

const STEP_META: Record<Step, { title: string; description: string }> = {
  property: {
    title: 'Find your property',
    description: 'Search by street address and select your home.',
  },
  selected: {
    title: 'Property selected',
    description: 'Confirm this is the home you want to claim.',
  },
  confirm: {
    title: 'Confirm & submit',
    description: 'Review the details, then submit your claim for review.',
  },
  done: {
    title: 'Claim submitted',
    description: 'We’ll notify you once your ownership verification is reviewed.',
  },
}

export function ClaimPropertyWizard({
  initialPropertyId,
  onComplete,
  onCancel,
}: {
  initialPropertyId?: string | null
  onComplete?: () => void
  onCancel?: () => void
}) {
  const { user, profile } = useAuth()
  const verified = Boolean(profile?.isIdentityVerified)
  const accountEmail = (user?.email ?? profile?.email ?? '').trim()

  const [step, setStep] = useState<Step>('property')
  const [property, setProperty] = useState<Property | null>(null)
  const [loadingProperty, setLoadingProperty] = useState(Boolean(initialPropertyId))
  const [queryText, setQueryText] = useState('')
  const [suggestions, setSuggestions] = useState<PropertySuggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!initialPropertyId) return
    let cancelled = false
    ;(async () => {
      setLoadingProperty(true)
      try {
        const snap = await getDoc(doc(db, 'properties', initialPropertyId))
        if (cancelled) return
        if (snap.exists()) {
          setProperty(parseProperty(snap.id, snap.data() as Record<string, unknown>))
          setStep('selected')
        }
      } finally {
        if (!cancelled) setLoadingProperty(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [initialPropertyId])

  useEffect(() => {
    const q = queryText.trim()
    if (step !== 'property' || q.length < 3) {
      setSuggestions([])
      setSearching(false)
      return
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      setSearching(true)
      try {
        const data = await searchPropertiesByPrefix(q)
        if (!cancelled) setSuggestions(data.properties ?? [])
      } catch {
        if (!cancelled) setSuggestions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [queryText, step])

  if (!verified) {
    return <SoftIdentityGate />
  }

  async function selectSuggestion(item: PropertySuggestion) {
    setQueryText('')
    setSuggestions([])
    setError(null)
    setLoadingProperty(true)
    try {
      const snap = await getDoc(doc(db, 'properties', item.id))
      if (snap.exists()) {
        setProperty(parseProperty(snap.id, snap.data() as Record<string, unknown>))
      } else {
        setProperty({
          id: item.id,
          baths: null,
          beds: null,
          cars: null,
          fullAddress: item.fullAddress,
          geohash_8: '',
          hasActiveBiddles: false,
          landSize: 0,
          location: { latitude: 0, longitude: 0 },
          postCode: item.postCode ?? '',
          propertyType: '',
          state: item.state ?? '',
          streetName: '',
          streetNumber: '',
          streetType: '',
          suburb: item.suburb ?? '',
          unitNumber: '',
          createdAt: null,
        })
      }
      setStep('selected')
    } finally {
      setLoadingProperty(false)
    }
  }

  function goBack() {
    setError(null)
    if (step === 'property') {
      onCancel?.()
      return
    }
    if (step === 'selected') {
      setProperty(null)
      setQueryText('')
      setSuggestions([])
      setStep('property')
      return
    }
    if (step === 'confirm') {
      setStep('selected')
    }
  }

  function goNext() {
    setError(null)
    if (step === 'selected' && property) {
      setStep('confirm')
    }
  }

  async function onSubmitClaim() {
    if (!property) return
    if (!accountEmail) {
      setError('Your account has no email. Add one in Settings before claiming a property.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { docID } = await initializeOwnershipVerification({
        propertyID: property.id,
        userProvidedEmail: accountEmail,
      })
      await finalizeOwnershipVerification({ documentID: docID })
      setStep('done')
    } catch (err) {
      setError(callableErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const stepIndex = STEPS.indexOf(step)
  const meta = STEP_META[step]
  const addressParts = property ? splitAddressDisplay(property.fullAddress) : null
  const progressIndex = step === 'done' ? 2 : Math.min(stepIndex, 2)

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted">
          Step {Math.min(stepIndex + 1, 3)} of 3
        </p>
        <h2 className="mt-1 text-xl font-bold text-navy">{meta.title}</h2>
        <p className="mt-1 text-sm text-muted">{meta.description}</p>
        <div className="mt-3 flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={
                i <= progressIndex
                  ? 'h-1 flex-1 rounded-full bg-biddle-blue'
                  : 'h-1 flex-1 rounded-full bg-border'
              }
            />
          ))}
        </div>
      </div>

      {step === 'property' ? (
        <div className="space-y-3">
          {loadingProperty ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <Input
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                placeholder="Search your address…"
                className="pl-10 pr-10"
                autoFocus
              />
              {searching ? (
                <Spinner className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              ) : null}
              {suggestions.length > 0 ? (
                <ul className="absolute left-0 right-0 z-50 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-border bg-white py-1 shadow-2xl">
                  {suggestions.map((item) => {
                    const parts = splitAddressDisplay(item.fullAddress)
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          className="w-full px-4 py-3 text-left text-sm hover:bg-surface"
                          onClick={() => void selectSuggestion(item)}
                        >
                          <span className="block font-medium text-navy">{parts.street}</span>
                          {parts.locality ? (
                            <span className="block text-xs text-muted">{parts.locality}</span>
                          ) : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {step === 'selected' && addressParts ? (
        <div className="flex items-center gap-4 rounded-xl bg-surface px-4 py-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-navy">{addressParts.street}</p>
            {addressParts.locality ? (
              <p className="mt-0.5 text-sm text-muted">{addressParts.locality}</p>
            ) : null}
          </div>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[3px] border-white bg-success shadow-md ring-1 ring-success/20">
            <Check className="h-5 w-5 text-white" strokeWidth={3} aria-hidden />
          </div>
        </div>
      ) : null}

      {step === 'confirm' ? (
        <div className="space-y-3 text-sm">
          <div className="rounded-xl bg-surface px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">Property</p>
            {addressParts ? (
              <>
                <p className="mt-1 font-semibold text-navy">{addressParts.street}</p>
                {addressParts.locality ? (
                  <p className="text-muted">{addressParts.locality}</p>
                ) : null}
              </>
            ) : null}
          </div>
          <p className="text-muted">
            Submitting starts ownership verification. You’ll be notified when it’s reviewed.
          </p>
        </div>
      ) : null}

      {step === 'done' ? (
        <div className="space-y-3 text-sm">
          <p className="text-muted">
            Your claim for{' '}
            <span className="font-medium text-navy">
              {property ? formatAddressDisplay(property.fullAddress) : 'this property'}
            </span>{' '}
            is in review.
          </p>
          <p className="text-muted">
            Received a letter instead?{' '}
            <Link to="/o" className="text-biddle-blue underline" onClick={onCancel}>
              Enter your campaign code
            </Link>
            .
          </p>
        </div>
      ) : null}

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <div className="flex flex-wrap justify-between gap-2 border-t border-border pt-4">
        {step === 'done' ? (
          <>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Close
            </Button>
            <Button type="button" variant="primary" onClick={onComplete ?? onCancel}>
              Done
            </Button>
          </>
        ) : step === 'property' ? (
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        ) : (
          <>
            <Button type="button" variant="ghost" onClick={goBack} disabled={busy}>
              Back
            </Button>
            {step === 'confirm' ? (
              <Button
                type="button"
                variant="primary"
                disabled={busy}
                onClick={() => void onSubmitClaim()}
              >
                {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                Submit claim
              </Button>
            ) : (
              <Button type="button" variant="primary" onClick={goNext}>
                Next
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
